import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Alert from '../../components/ui/Alert.jsx';
import { HowThisPageWorks, Loading } from '../../components/ui/index.jsx';
import ScoreGauge from '../../components/ui/ScoreGauge.jsx';
import { PartBar, Trend } from '../Dashboard.jsx';
import { fmtMinutes, itMailto, nextStep, planSteps } from './planSteps.js';

const DONE_SHOWN = 3;

function Tick() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.5 2.5 4.5-5" />
    </svg>
  );
}

/** The step to do now: what it is, why, and two ways to get it done. */
function NextStep({ step, number, organisationName }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const send = () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setError('Enter their email address.'); return; }
    window.location.href = itMailto(step, organisationName).replace('mailto:?', `mailto:${encodeURIComponent(email.trim())}?`);
  };
  return (
    <section className="card plan-next" aria-labelledby="next-h">
      <div className="plan-next-head">
        <span className="plan-number">{number}</span>
        <div>
          <p className="plan-kicker">Next step · about {fmtMinutes(step.minutes)}</p>
          <h2 id="next-h">{step.title}</h2>
          <p className="plan-why">{step.why}</p>
        </div>
      </div>
      <div className="plan-ways">
        {step.technical && (
          <div className="plan-way">
            <h3>Someone else looks after your website or email</h3>
            <p className="muted">We&rsquo;ll write them an email with the change. You&rsquo;ll see it ticked off here once it&rsquo;s done.</p>
            <div className="field">
              <label htmlFor="it-email">Their email address</label>
              <input id="it-email" type="email" value={email} placeholder="it@yourcompany.com" aria-invalid={error ? 'true' : undefined}
                onChange={(e) => { setEmail(e.target.value); setError(''); }} />
              {error && <p className="field-error" role="alert">{error}</p>}
            </div>
            <button type="button" className="btn btn-primary btn-block" onClick={send}>Write them the email</button>
          </div>
        )}
        <div className="plan-way">
          <h3>{step.technical ? 'I’ll do it myself' : 'Do it now'}</h3>
          <p className="muted">{step.hint || step.label}</p>
          {step.action && <Link to={step.action.to} className="btn btn-secondary btn-block">{step.action.label}</Link>}
        </div>
      </div>
      <p className="muted small">Done it? We check for you and tick it off, so there&rsquo;s nothing else to do.</p>
    </section>
  );
}

export default function Plan() {
  const { organisation } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    if (!organisation) return;
    api(`/portal/organisations/${organisation.id}/dashboard`).then(setData).catch((err) => setError(err.message));
  }, [organisation]);

  if (error) return <div className="page"><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;

  const { score, history } = data;
  const steps = planSteps(score);
  const done = steps.filter((s) => s.done);
  const next = nextStep(steps);
  const rest = steps.filter((s) => !s.done && s !== next).sort((a, b) => b.gain - a.gain);
  const left = [next, ...rest].filter(Boolean).reduce((t, s) => t + s.minutes, 0);
  const pct = steps.length ? (done.length / steps.length) * 100 : 0;
  const shownDone = showDone ? done : done.slice(0, DONE_SHOWN);

  return (
    <div className="plan-layout">
      <div className="plan-main">
        <div className="page-header-top"><span /><HowThisPageWorks topic="protection-plan" /></div>
        <div>
          <h1>Your protection plan</h1>
          <p className="plan-lead">Each step closes one way scammers use your name. Do them in any order and we’ll check each one for you.</p>
        </div>

        <div className="card plan-progress">
          <div className="plan-progress-bar">
            <p><strong>{done.length} of {steps.length} done</strong></p>
            <div className="plan-bar" role="img" aria-label={`${done.length} of ${steps.length} steps done`}><span style={{ width: `${pct}%` }} /></div>
          </div>
          <p className="muted">{next ? `About ${fmtMinutes(left)} left. The next step makes the biggest difference.` : 'Every step is done. We keep checking in case anything changes.'}</p>
        </div>

        {done.length > 0 && (
          <section className="card plan-list" aria-label="Done">
            <ul>
              {shownDone.map((s) => (
                <li key={s.id} className="is-done"><span className="plan-tick"><Tick /></span><span className="plan-step-title">{s.title}</span><span className="plan-done-label">Done</span></li>
              ))}
            </ul>
            {done.length > DONE_SHOWN && (
              <button type="button" className="btn btn-small btn-secondary" onClick={() => setShowDone(!showDone)}>
                {showDone ? 'Show fewer' : `Show all ${done.length} done`}
              </button>
            )}
          </section>
        )}

        {next && <NextStep step={next} number={done.length + 1} organisationName={organisation.name} />}

        {rest.length > 0 && (
          <section className="card plan-list" aria-labelledby="later-h">
            <h2 id="later-h">Still to do</h2>
            <ul>
              {rest.map((s) => (
                <li key={s.id}>
                  <span className="plan-circle" aria-hidden="true" />
                  <div className="plan-step-text">
                    <strong>{s.title}</strong>
                    <span className="muted">{s.why}</span>
                  </div>
                  <span className="muted plan-minutes">{fmtMinutes(s.minutes)}</span>
                  {s.action && <Link to={s.action.to} className="btn btn-small btn-secondary">Start</Link>}
                </li>
              ))}
            </ul>
          </section>
        )}

        <details className="card plan-detail">
          <summary>The detailed score (for your IT person)</summary>
          <p className="muted small">Everything here is something you control. Being targeted never lowers your score; leaving an alert unanswered for over a week does.</p>
          <div className="parts">
            {score.parts.map((p) => <PartBar key={p.key} part={p} />)}
          </div>
        </details>
      </div>

      <aside className="plan-side">
        <section className="card plan-level" aria-labelledby="level-h">
          <h2 id="level-h">Protection level</h2>
          <ScoreGauge score={score.score} label={score.band.label} />
          {next && next.gain > 0 && <p className="muted">The next step alone adds up to {next.gain} point{next.gain === 1 ? '' : 's'}.</p>}
          {score.test_mode && <p className="muted small">While you&rsquo;re being set up, this can&rsquo;t go above 40.</p>}
          {score.deduction > 0 && <p className="muted small">Includes −{score.deduction} for alerts left unanswered for over a week.</p>}
          <Trend history={history} />
        </section>
        <section className="card plan-why-card" aria-labelledby="why-h">
          <h2 id="why-h">Why these steps?</h2>
          <p>Most event scams copy the organiser: a fake invoice, a hotel offer, a ticket page. Each step closes one way in, and makes it easier for us to say “that&rsquo;s not really them”.</p>
        </section>
      </aside>
    </div>
  );
}
